import { useMemo, useState } from 'react'
import { useAccount } from 'wagmi'
import { T } from '@/lib/theme'
import { Button, Input, Panel, SectionLabel, Badge, DistributionBar } from '@/components/ui'
import { ADDRESSES } from '@/lib/contracts'
import { fmt } from '@/lib/utils'
import gaugeAbi from '@/lib/abis/GaugeController.json'
import { gaugeLabel } from '@/lib/gaugeLabel'
import { orderedPairSymbols } from '@/lib/tokenTags'
import { PairGlyph } from '@/components/dashboard/TokenGlyph'
import { useAction } from '@/hooks/useAction'
import { useGaugeVote } from '@/hooks/useGaugeVote'
import { useVaultsList } from '@/hooks/useVaultsList'

const PALETTE = [T.accent, T.cyan, T.purple, T.positive, T.warning, T.accentDeep]

function countdown(endsAtSec) {
  const rem = Math.max(endsAtSec - Math.floor(Date.now() / 1000), 0)
  const d = Math.floor(rem / 86_400)
  const h = Math.floor((rem % 86_400) / 3_600)
  const m = Math.floor((rem % 3_600) / 60)
  return d > 0 ? `${d}d ${h}h` : h > 0 ? `${h}h ${m}m` : `${m}m`
}

/// The token symbols behind a gauge, for its pair glyph - a ALM vault gauge resolves to its two
/// tokens (in display order, quote asset last, so the glyphs match the pair name); a reserve/guardian
/// gauge has none (a lettered fallback stands in).
function gaugeSymbols(vault, vaults) {
  const v = (vault || '').toLowerCase()
  const vt = (vaults ?? []).find((x) => (x.address || '').toLowerCase() === v)
  if (!vt?.token0?.symbol || !vt?.token1?.symbol) return null
  return orderedPairSymbols(vt.token0.address, vt.token1.address, vt.token0.symbol, vt.token1.symbol)
}

function GaugeGlyph({ symbols, label }) {
  if (symbols) return <PairGlyph symbols={symbols} size={26} />
  return (
    <div style={{
      width: 26, height: 26, borderRadius: '50%', flexShrink: 0, display: 'grid', placeItems: 'center',
      background: T.accentDim, border: `1px solid ${T.border}`, color: T.accent, fontSize: 10, fontWeight: 800,
    }}>{(label || '?').replace(/[^A-Za-z0-9]/g, '').slice(0, 2).toUpperCase()}</div>
  )
}

function GaugeCard({ label, symbols, weightBps, barPct, raw, myPct, active, onChange }) {
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', gap: 12, padding: '14px 16px',
      background: active ? T.glassSurfaceHover : T.glassSurface,
      border: `1px solid ${active ? `${T.accent}55` : T.border}`,
      borderLeft: `2px solid ${active ? T.accent : 'transparent'}`,
      transition: 'background 0.18s ease, border-color 0.18s ease',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <GaugeGlyph symbols={symbols} label={label} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ color: T.text, fontSize: 15, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</div>
          <div style={{ color: T.textDim, fontSize: 12 }}>now {(weightBps / 100).toFixed(1)}% of emissions</div>
        </div>
        <div style={{ width: 76, flexShrink: 0 }}>
          <Input value={raw} type="text" placeholder="0" onChange={onChange} style={{ padding: '8px 10px' }} />
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ flex: 1, height: 6, background: 'rgba(255,255,255,0.06)', overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${barPct}%`, background: active ? T.accent : T.accentDeep, transition: 'width 0.3s ease' }} />
        </div>
        <span className="mono" style={{ width: 42, textAlign: 'right', fontSize: 13, fontWeight: 700, color: myPct > 0 ? T.positive : T.textDim }}>
          {myPct.toFixed(0)}%
        </span>
      </div>
    </div>
  )
}

/// The ve(3,3) gauge vote (GaugeController). A holder allocates voting power across gauges for the
/// CURRENT weekly epoch; the tally sets emission weights, and votes reset each epoch. Votable gauges
/// are a card grid (pair glyph, live emission weight, relative allocator) with a live allocation
/// preview; the fixed gauges (a lending reserve or guardian pool pinned off the top) sit below as
/// compact chips. One Cast action commits the epoch's allocation.
export function VotePanel({ d, onTx }) {
  const { address: user } = useAccount()
  const action = useAction()
  const gc = ADDRESSES.GAUGE_CONTROLLER
  const { farms, epochEndsAt, userPower, notDeployed, refetch } = useGaugeVote(user)
  const { vaults } = useVaultsList()

  // Relative weight per votable gauge, as the holder types it. The contract splits power by the SUM
  // of these, so they are relative, not required to total 100.
  const [weights, setWeights] = useState({})
  const votable = useMemo(() => farms.filter((f) => f.votable), [farms])
  const fixed = useMemo(() => farms.filter((f) => !f.votable && f.fixedBps > 0), [farms])
  const sumW = useMemo(() => votable.reduce((a, f) => a + (Number(weights[f.id]) || 0), 0), [weights, votable])
  const maxWeight = useMemo(() => votable.reduce((m, f) => Math.max(m, f.weightBps), 0), [votable])

  if (notDeployed) return null

  const hasPower = userPower > 0n
  const entries = votable.filter((f) => (Number(weights[f.id]) || 0) > 0)
  const segments = entries.map((f, i) => ({
    label: gaugeLabel(f.vault, d, vaults),
    pct: sumW > 0 ? ((Number(weights[f.id]) || 0) / sumW) * 100 : 0,
    color: PALETTE[i % PALETTE.length],
  }))

  async function cast() {
    if (entries.length === 0) return
    const ids = entries.map((f) => BigInt(f.id))
    const ws = entries.map((f) => BigInt(Math.floor(Number(weights[f.id]))))
    const res = await action.run({
      calls: [{ address: gc, abi: gaugeAbi, functionName: 'vote', args: [ids, ws] }],
      successMsg: 'Votes cast for this epoch.',
    })
    if (res) { setWeights({}); await refetch(); onTx?.() }
  }
  async function reset() {
    const res = await action.run({ calls: [{ address: gc, abi: gaugeAbi, functionName: 'resetVote', args: [] }], successMsg: 'Votes cleared.' })
    if (res) { setWeights({}); await refetch(); onTx?.() }
  }
  async function sync() {
    const res = await action.run({ calls: [{ address: gc, abi: gaugeAbi, functionName: 'pushWeights', args: [] }], successMsg: 'Emission weights synced to this epoch.' })
    if (res) { await refetch(); onTx?.() }
  }

  return (
    <Panel style={{ padding: 32 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
        <SectionLabel>Vote</SectionLabel>
        <Badge color={T.accent}>Resets in {countdown(epochEndsAt)}</Badge>
      </div>
      <p style={{ color: T.textDim, fontSize: 14, lineHeight: 1.6, margin: '10px 0 20px', maxWidth: 640 }}>
        Direct where HORIZON emissions and vault fees flow this week. Your voting power splits across the
        gauges you choose. Votes reset every epoch, so re-cast each week to keep steering the flow.
      </p>

      {!hasPower && (
        <div style={{
          color: T.textMuted, fontSize: 14, marginBottom: 20, padding: '12px 16px',
          background: T.accentDim,
        }}>
          Lock HORIZON above to earn voting power, then allocate it here.
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 12 }}>
        <SectionLabel>Gauges</SectionLabel>
        {hasPower && (
          <span className="mono" style={{ color: T.textDim, fontSize: 12 }}>
            {fmt(Number(userPower) / 1e18)} veHORIZON{entries.length > 0 ? ` · ${entries.length} selected` : ''}
          </span>
        )}
      </div>

      {votable.length === 0 && <div style={{ color: T.textDim, fontSize: 14, marginBottom: 18 }}>No votable gauges yet. They appear as vault and reserve farms are added.</div>}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 12, marginBottom: 20 }}>
        {votable.map((f) => {
          const raw = weights[f.id] ?? ''
          const myPct = sumW > 0 ? ((Number(weights[f.id]) || 0) / sumW) * 100 : 0
          return (
            <GaugeCard
              key={f.id}
              label={gaugeLabel(f.vault, d, vaults)}
              symbols={gaugeSymbols(f.vault, vaults)}
              weightBps={f.weightBps}
              barPct={maxWeight > 0 ? (f.weightBps / maxWeight) * 100 : 0}
              raw={raw}
              myPct={myPct}
              active={(Number(raw) || 0) > 0}
              onChange={(v) => setWeights((w) => ({ ...w, [f.id]: v.replace(/[^0-9]/g, '') }))}
            />
          )
        })}
      </div>

      {entries.length > 0 && (
        <div style={{ marginBottom: 20, padding: '16px 18px', background: T.glassSurface, border: `1px solid ${T.border}` }}>
          <SectionLabel style={{ marginBottom: 12 }}>Your allocation</SectionLabel>
          <DistributionBar segments={segments} />
        </div>
      )}

      {fixed.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <SectionLabel style={{ marginBottom: 10 }}>Pinned gauges · not votable</SectionLabel>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {fixed.map((f) => (
              <div key={f.id} style={{
                display: 'inline-flex', alignItems: 'center', gap: 8, padding: '6px 12px',
                background: T.glassSurface, border: `1px solid ${T.border}`,
              }}>
                <span style={{ color: T.textMuted, fontSize: 13 }}>{gaugeLabel(f.vault, d, vaults)}</span>
                <span className="mono" style={{ color: T.textDim, fontSize: 12, fontWeight: 700 }}>{(f.fixedBps / 100).toFixed(1)}%</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <Button onClick={cast} disabled={!hasPower || entries.length === 0 || action.pending}>
          Cast votes
        </Button>
        <Button variant="ghost" onClick={reset} disabled={!hasPower || action.pending}>Reset</Button>
        <Button variant="ghost" onClick={sync} disabled={action.pending} title="Apply this epoch's votes to emission weights. Anyone can run this.">
          Sync weights
        </Button>
      </div>
      <p style={{ color: T.textDim, fontSize: 12, lineHeight: 1.6, margin: '12px 0 0', maxWidth: 640 }}>
        Weights are relative: your power divides across the gauges you set, in proportion. Sync weights
        applies the current tally to emissions and is permissionless, so a keeper usually runs it.
      </p>
    </Panel>
  )
}
